import { Injectable, computed, signal } from '@angular/core';

import { GoalKey } from '@cpd-app/features/academic-goal/components/goal-set-dialog/goal-set-dialog.component';

/**
 * WHICH GOAL WAS JUST SET, WHEREVER IT WAS SET FROM.
 *
 * `cpd-goal-set-dialog` is the confirmation the product shows after a goal:
 * the checklist of all four, the licensure one marked optional, and the
 * leaving-or-staying question. It was reachable from one place, the schools
 * and programs step, so setting a CAREER goal from the occupation hero got a
 * different dialog entirely: a blue band, a single card, one button, and none
 * of the checklist. Two confirmations for the same event.
 *
 * The state moves here so both surfaces raise the same one. It is a signal on
 * a root service rather than an input, because the two places that set a goal
 * are a parent and its own child component, and passing it down would make
 * the child's dialog the parent's business.
 */
@Injectable({ providedIn: 'root' })
export class GoalCelebrationService {
  /** The goal just set, or null when there is nothing to celebrate. */
  public readonly justSet = signal<GoalKey | null>(null);

  /**
   * GR-15. The dialog is open for REVIEW rather than after a goal was set.
   *
   * Two signals rather than one, because the two openings are different
   * events and the dialog says different things. `justSet` answers "which goal
   * did you just choose", and the heading reports it. Pressing 3/3 in the top
   * bar sets no goal, so there is nothing for that heading to report -- folding
   * review into `justSet` would mean inventing a goal to celebrate.
   */
  public readonly isReviewing = signal(false);

  /** True whenever the dialog should be on screen, however it was opened. */
  public readonly isOpen = computed(
    () => this.justSet() !== null || this.isReviewing()
  );

  /**
   * CPD-664. Whether a PAGE is currently hosting the dialog itself.
   *
   * The dialog is mounted twice: once in the app shell, so the 3/3 counter can
   * raise it from anywhere, and once in the schools-and-programs step, whose
   * copy is wired to that page -- it scrolls to the school step rather than
   * navigating, and it runs the account-creation prompt an anonymous member
   * needs before a Goals Report exists to open.
   *
   * Both read this service, so on the occupation page BOTH rendered: two
   * scrims stacked, two elements with `aria-modal`, and a screen reader
   * announcing the same dialog twice. It was invisible to the eye because the
   * two overlays sit at identical coordinates.
   *
   * So a page that mounts its own says so, and the shell stands down. A count
   * rather than a flag because route changes overlap: the incoming component
   * is created before the outgoing one is destroyed, and a boolean would be
   * released by the page that is leaving.
   */
  private readonly pageHosts = signal(0);

  /** True while some page is mounting the dialog itself. */
  public readonly hostedByPage = computed(() => this.pageHosts() > 0);

  public claimHost(): void {
    this.pageHosts.update((count) => count + 1);
  }

  public releaseHost(): void {
    this.pageHosts.update((count) => Math.max(0, count - 1));
  }

  /**
   * A11Y.1 (28 Sep rescan, 2.4.3). Where focus goes when the dialog is
   * dismissed, when the caller knows better than `document.activeElement`
   * does at the moment the dialog opens.
   *
   * The occupation page's Set Career Goal is the case: the button is disabled
   * while the goal saves, which drops focus to the page, so by the time the
   * dialog opens the element that opened it no longer has focus. The page
   * hands it over here instead. Not reactive: only the dialog reads it, once,
   * as it closes.
   */
  public returnFocusTo: HTMLElement | null = null;

  public celebrate(key: GoalKey, returnFocusTo: HTMLElement | null = null): void {
    this.returnFocusTo = returnFocusTo;
    this.justSet.set(key);
  }

  /** GR-15. What the 3/3 counter does. */
  public review(returnFocusTo: HTMLElement | null = null): void {
    this.returnFocusTo = returnFocusTo;
    this.isReviewing.set(true);
  }

  public dismiss(): void {
    // The dialog took its copy when it opened. Left here, the next dialog
    // (a school goal after a program one) would send focus to this one's
    // target instead of its own opener.
    this.returnFocusTo = null;
    this.justSet.set(null);
    this.isReviewing.set(false);
  }
}
